<template>
  <div class="tla-modal" v-bind:class="{ 'tla-modal--open': modalOpen }">
    <div class="tla-modal__content">
      <div class="tla-modal__close" @click="toggleModal()">Close <img src="@/assets/images/modal-close.svg"/></div>
      <div v-if="currentOverlayStep === 1">
        <div class="tla-modal__header">
          Enter Registration Number
        </div>
        <LoaderCardComponent :text="'Retrieving Data'" v-show="showLoader"/>
        <RegistrationLookupDetailsComponent
          v-show="!showLoader"
          :validationScope="validationScope"
          :isOverlay="true"
          :nextButtonText="'Find'"
          :registrationLabel="''"
          @showLoader="showLoader = $event"
          @createManualVehicleModules="createManualVehicleModules($event)" />
      </div>
      <div v-if="currentOverlayStep === 2">
        <StepHeaderComponent :inLine="true" :headerText="'Vehicle Found'" :subHeaderText="'Please confirm if this your model'" />
        <DisabledRegistrationInputComponent :forceShowInputTick="true" />
        <RegistrationLookupResultComponent @createManualVehicleModules="createManualVehicleModules($event)" ref="regLookupResultComponent" @closeOverlay="toggleModal()"/>
      </div>
      <div v-if="currentOverlayStep === 3">
        <StepHeaderComponent :inLine="true" :headerText="'Manual Vehicle Entry'" :subHeaderText="'Please input your existing vehicle data'" />
        <DisabledRegistrationInputComponent v-if="valuationRequest.registration.length > 0" :forceShowInputTick="true" />
        <ManualVehicleComponent @closeOverlay="toggleModal()" ref="manualVehicleComponent" :moduleKey="manualVehicleModuleKey" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  Component, Vue, Prop, Ref, Inject
} from 'vue-property-decorator';

import LoaderCardComponent from '@/components/ui/LoaderCardComponent.vue';
import StepHeaderComponent from '@/components/general/StepHeaderComponent.vue';
import DisabledRegistrationInputComponent from '@/components/vehicle/lookup/DisabledRegistrationInputComponent.vue';
import RegistrationLookupResultComponent from '@/components/vehicle/lookup/RegistrationLookupResultComponent.vue';
import RegistrationLookupDetailsComponent from '@/components/vehicle/lookup/RegistrationLookupDetailsComponent.vue';
import ManualVehicleComponent from '@/components/vehicle/ManualVehicleComponent.vue';
import { scrollService } from '@/services/ScrollService';
import Valuation from '@/models/valuation/Valuation';
import VehicleType from '@/enums/VehicleType';
import VehicleLevel from '@/enums/VehicleLevel';
import VehicleStatusType from '@/enums/VehicleStatusType';
import VehicleModuleOptions from '@/models/VehicleModuleOptions';
import { Validator } from 'vee-validate';

@Component({
  components: {
  StepHeaderComponent,
  RegistrationLookupDetailsComponent,
  LoaderCardComponent,
  RegistrationLookupResultComponent,
  DisabledRegistrationInputComponent,
  ManualVehicleComponent
  }
  })
export default class RegistrationOverlayComponent extends Vue {
  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: 'overlayValidationScope' })
  public validationScope!: string;

  @Prop({ default: 'tla-registration-group' })
  public scrollElementId!: string;

  @Prop({ default: false })
  public goToNextStep!: boolean;

  @Prop({ default: VehicleType.Car })
  public vehicleType!: VehicleType;

  @Prop({ default: VehicleLevel.Range })
  public vehicleLevel!: VehicleLevel;

  @Prop({ default: VehicleStatusType.PartExchange })
  public vehicleStatusType!: VehicleStatusType;

  @Ref()
  public readonly manualVehicleComponent!: ManualVehicleComponent;

  @Ref()
  public readonly regLookupResultComponent!: RegistrationLookupResultComponent;

  private showLoader: boolean = false;

  private modalOpen: boolean = false;

  public get currentOverlayStep(): number {
    return this.$store.getters['enquiryModule/currentOverlayStep'];
  }

  public get currentFormStep(): number {
    return this.$store.getters['enquiryModule/currentStep'];
  }

  public get valuationRequest() {
    return this.$store.getters['vehicleValuationModule/valuationRequest'];
  }

  public get invalid(): boolean {
    return this.$validator.errors.items.filter((x) => x.scope === this.validationScope).length > 0;
  }

  public toggleModal(): void {
    if (this.modalOpen) {
      this.closeModal();
    } else {
      this.openModal();
    }
  }

  public openModal(): void {
    scrollService.disableBodyScroll();
    this.modalOpen = !this.modalOpen;
  }

  public closeModal(): void {
    // Set vehicle.
    if (this.currentOverlayStep === 2 && this.regLookupResultComponent !== undefined) {
      this.regLookupResultComponent.setVehicle();
    }

    if (this.currentOverlayStep === 3 && this.manualVehicleComponent !== undefined) {
      this.manualVehicleComponent.setVehicle();
    }

    scrollService.allowBodyScroll();
    scrollService.scrollToElementId(this.scrollElementId);

    // Close or Skip to next Form Step.
    const isValid = this.$store.getters['vehicleValuationModule/isRegistrationLookupDataSet'];
    if (isValid && this.goToNextStep) {
      this.$store.dispatch('enquiryModule/submitNextStep', this.currentFormStep + 1);
    }

    this.modalOpen = !this.modalOpen;
  }

  public get manualVehicleModuleKey(): string {
    return this.$store.getters['vehicleValuationModule/moduleKey'];
  }

  public createManualVehicleModules(nextStep: number): void {
    this.$store.dispatch(
      'vehicleManagementModule/submitCreateNewModuleInstances',
      new VehicleModuleOptions(this.vehicleType, this.vehicleLevel, this.vehicleStatusType, false)
    ).then((key: string) => {
      this.$store.dispatch('vehicleValuationModule/submitIsManualVehicle', true);
      this.$store.dispatch('vehicleValuationModule/submitRegistration');
      this.$store.dispatch('vehicleValuationModule/submitModuleKey', key)
        .then(() => {
          this.$store.dispatch('enquiryModule/submitOverlayStep', nextStep);
        });
    });
  }

  public created() {
    this.$store.dispatch('vehicleValuationModule/submitValuationExtraData', new Valuation());
    this.$validator.validateAll(this.validationScope);
  }
}
</script>
